import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AuthService,
  DataService,
  ToasterService,
} from '../../../shared/services';
import { Router } from '@angular/router';
import { GridOptions } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AccountRoleModel } from '../../../shared/models';
import { ConfirmModalComponent } from '../../../shared/modals';
import { GridComponent } from '../../../shared/';
import { RoleEditComponent } from './edit/edit.component';

import * as _ from 'lodash';

import { BreadCrumbService } from '../../../shared/services/breadcrumb.service';

@Component({
  selector: 'app-role',
  templateUrl: './role.component.html',
  styleUrls: ['./role.component.scss'],
})
export class RoleListComponent implements OnInit {
  @ViewChild(GridComponent, { static: true }) activityGrid?: GridComponent;

  // Available items
  items: any[] = [];
  selectedNodes: any[] = [];
  parentFiltered?: any;
  // UI flag
  isLoading = false;
  // Local grid options
  gridOptions?: GridOptions;
  // Modal ref pointer
  bsModalRef?: BsModalRef;

  roleReference = [
    {
      name: this.translate.instant(
        'headers.account_and_user_management.role.role_1'
      ),
      value: 1,
    },
    {
      name: this.translate.instant(
        'headers.account_and_user_management.role.role_2'
      ),
      value: 2,
    },
    {
      name: this.translate.instant(
        'headers.account_and_user_management.role.role_3'
      ),
      value: 3,
    },
  ];

  constructor(
    private translate: TranslateService,
    private data: DataService,
    private bsModalService: BsModalService,
    private toastr: ToasterService,
    public auth: AuthService,
    public router: Router,
    public breadCrumbService: BreadCrumbService
  ) {}

  /**
   */
  async ngOnInit(): Promise<void> {
    this.breadCrumbService.set(
      [
        {
          name: this.translate.instant('navigations.breadcrumbs.modules'),
          link: '/modules',
        },
        {
          name: this.translate.instant('navigations.breadcrumbs.parameters'),
          link: '/admin',
        },
        {
          name: this.translate.instant('navigations.breadcrumbs.user_roles'),
          link: '/admin/roles',
        },
      ],
      2
    );
    this.gridOptions = {
      getRowId: (row: any) => row.data.id,
      suppressRowClickSelection: true,
      rowSelection: 'multiple',
      rowHeight: 30,
      headerHeight: 30,
      animateRows: true,
      floatingFiltersHeight: 30,
      enableCellTextSelection: false,
      suppressContextMenu: true,
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        floatingFilter: true,
      },
      columnDefs: [
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.role'
          ),
          field: 'name',
          filter: 'agTextColumnFilter',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.id'
          ),
          field: 'id',
          hide: true,
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.asset'
          ),
          field: 'asset',
          cellRenderer: this.roleRenderer,
          filterParams: {
            valueFormatter: this.valueFormatter,
          },
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.fastpm'
          ),
          field: 'fastpm',
          cellRenderer: this.roleRenderer,
          filterParams: {
            valueFormatter: this.valueFormatter,
          },
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.location'
          ),
          field: 'location',
          cellRenderer: this.roleRenderer,
          filterParams: {
            valueFormatter: this.valueFormatter,
          },
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.routing'
          ),
          field: 'routing',
          cellRenderer: this.roleRenderer,
          filterParams: {
            valueFormatter: this.valueFormatter,
          },
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.fast_criticality'
          ),
          field: 'fastCriticality',
          cellRenderer: this.roleRenderer,
          filterParams: {
            valueFormatter: this.valueFormatter,
          },
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.fastsp'
          ),
          field: 'fastsp',
          cellRenderer: this.roleRenderer,
          filterParams: {
            valueFormatter: this.valueFormatter,
          },
        },
        {
          headerName: this.translate.instant(
            'headers.account_and_user_management.role.fastlc'
          ),
          field: 'fastlc',
          cellRenderer: this.roleRenderer,
          filterParams: {
            valueFormatter: this.valueFormatter,
          },
        },
      ],
    };
    await this.loadItems();
  }

  roleRenderer = (params: any): string => {
    let cssClass = '';
    if (params.value === 1) {
      cssClass = 'badge-light';
    } else if (params.value === 2) {
      cssClass = 'badge-warning';
    } else if (params.value === 3) {
      cssClass = 'badge-success';
    }
    return (
      '<span class="badge badge-lg ' +
      cssClass +
      '">' +
      this.translate.instant(
        'headers.account_and_user_management.role.role_' + params.value
      ) +
      '</span>'
    );
  };

  valueFormatter = (params: any): string => {
    return this.translate.instant(
      'headers.account_and_user_management.role.role_' + params.value
    );
  };

  /**
   */
  async loadItems(silent = false): Promise<void> {
    if (!silent) {
      this.isLoading = true;
    }
    this.items = await this.data
      .by('api/accounts/roles', {
        criteria: {
          account: this.auth.getAccountId(),
        },
      })
      .toPromise();
    this.activityGrid?.gridApi?.sizeColumnsToFit();
    this.activityGrid?.uncheckAll();
    if (!silent) {
      this.isLoading = false;
    }
  }

  onFilterChanged(event: any): void {}

  onSelectionChanged(event: any): void {
    this.selectedNodes = event;
  }

  onRowClicked(event: any): void {
    this.showEditModal([new AccountRoleModel(event)]);
  }

  /**
   * @param items
   */
  async showEditModal(items?: AccountRoleModel[]): Promise<void> {
    this.bsModalRef = this.bsModalService.show(RoleEditComponent, {
      initialState: {
        items,
        roleReference: this.roleReference,
      },
    });
    this.bsModalRef.content.confirmFn = () => {
      this.loadItems();
      this.toastr.showSuccess();
    };
  }

  /**
   */
  showMassDeleteConfirm(): void {
    this.bsModalRef = this.bsModalService.show(ConfirmModalComponent, {
      initialState: {
        title: 'navigations.delete',
        content: '',
      },
    });
    this.bsModalRef.content.confirmFn = () => {
      const ids = _.map(this.selectedNodes, (el) => el.id);
      this.data.post('api/accounts/roles/delete-all', ids).subscribe(
        (res) => {
          this.loadItems();
          this.toastr.showSuccess('messages.delete');
        },
        (err) => {
          this.toastr.showError();
        }
      );
      this.bsModalRef?.hide();
    };
    this.bsModalRef.content.cancelFn = () => {
      this.bsModalRef?.hide();
    };
  }

  myCellCallback = (params: any) => {
    return params.value;
  };
}
